import { DFPManager } from 'react-dfp';

// Transcend purpose that gates personalized advertising.
const ADVERTISING_PURPOSE = 'Advertising';

/**
 * Keeps GAM ad personalization in sync with the Transcend cookie consent state.
 *
 * Advanced Consent Mode: ads are served NON-personalized by default (the
 * DFPSlotsProvider is configured with personalizedAds={false} / cookieOption={false},
 * and the GcmAdvanced override in IndexPage lets those requests through before a
 * consent decision). When the user grants Advertising consent we switch GAM to
 * personalized ads + cookies and refresh the slots. On reject we stay
 * non-personalized (no refresh needed — that is already the served state).
 *
 * It also pushes a `consent_update` dataLayer event on every consent decision so
 * GTM-managed analytics tags can fire at the moment of consent.
 */
export default function initConsentAds() {
  if (typeof window === 'undefined') return;
  // Guard against duplicate listeners (e.g. HMR / re-mounts).
  if (window.__consentAdsInit) return;

  const { airgap } = window;
  if (!airgap || typeof airgap.ready !== 'function') return;
  window.__consentAdsInit = true;

  // Initial personalization state matches the DFPSlotsProvider default (non-personalized).
  let lastPersonalized = false;

  // Authoritatively set NPA + cookie options on the pubads service before refreshing.
  const applyGamPrivacy = personalized => {
    window.googletag = window.googletag || { cmd: [] };
    window.googletag.cmd.push(() => {
      const pubads = window.googletag.pubads();
      pubads.setRequestNonPersonalizedAds(personalized ? 0 : 1);
      pubads.setCookieOptions(personalized ? 0 : 1);
    });
  };

  const applyConsent = () => {
    let consent;
    try {
      consent = airgap.getConsent();
    } catch (e) {
      return;
    }
    const personalized = consent.purposes[ADVERTISING_PURPOSE] === true;

    // Let GTM-managed analytics tags fire at the moment of consent — only once the
    // user has actually made a decision (or it has synced), never on the pre-consent view.
    if (consent.confirmed) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'consent_update', advertising_consent: personalized });
    }

    // Only refresh when the personalization state actually changes.
    if (personalized === lastPersonalized) return;
    lastPersonalized = personalized;

    // Keep react-dfp's internal flags in sync, set the GAM privacy signals, then refresh.
    DFPManager.configurePersonalizedAds(personalized);
    DFPManager.configureCookieOption(personalized);
    applyGamPrivacy(personalized);
    DFPManager.refresh();
  };

  airgap.ready(() => {
    // Handle already-stored consent (e.g. returning users) then react to future changes.
    applyConsent();
    airgap.addEventListener('consent-change', applyConsent);
  });
}
